import { ReactNode, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export const ADMIN_RETURN_TO_KEY = "silversage.admin.returnTo";

/** Remember the admin URL a visitor asked for so login can send them back. */
export function rememberAdminReturnTo(path: string) {
  try {
    if (path.startsWith("/admin") && !path.startsWith("/admin/login")) {
      sessionStorage.setItem(ADMIN_RETURN_TO_KEY, path);
    }
  } catch {
    /* storage blocked — the router state fallback still applies */
  }
}

/**
 * Route-level gate for every admin page.
 *
 * Admin pages fire protected queries (payroll-engine, ledger, tax center…) from
 * effects and react-query hooks that run the moment the component mounts. If the
 * page mounts while the Supabase session is still hydrating, those calls go out
 * unauthenticated and the edge functions correctly answer 400 `not_signed_in`.
 *
 * Equally important: once a page IS mounted it must stay mounted. Returning to a
 * backgrounded tab triggers a token refresh; if the guard dropped back to a
 * loading/redirect state during that refresh, the whole admin subtree unmounted
 * and the admin lost their page, their editor tab and any unsaved edits.
 * So access is "sticky": after the first successful admin check the children
 * keep rendering until an explicit sign-out.
 */
export function AdminGuard({ children }: { children: ReactNode }) {
  const { user, isAdmin, loading } = useAuth();
  const location = useLocation();
  const grantedRef = useRef(false);

  if (user && isAdmin) grantedRef.current = true;
  // Only an explicit signed-out state revokes a granted session.
  if (grantedRef.current && (loading || user)) return <>{children}</>;
  if (grantedRef.current && !user && !loading) grantedRef.current = false;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!user) {
    const from = location.pathname + location.search + location.hash;
    rememberAdminReturnTo(from);
    return <Navigate to="/admin/login" replace state={{ from }} />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center space-y-2">
          <h1 className="font-heading text-xl font-bold text-foreground">
            You don't have access to this page
          </h1>
          <p className="text-sm text-muted-foreground">
            This area is limited to Silver Sage administrators. If you think this is a
            mistake, contact silversagehomeschoolhubs@gmail.com.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
